import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../const";
import { bankValidation } from "../validations/BankCreation";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";

const FORM = FORMS.BANK.BANK_MASTER.form;

const BankAddComponent = forwardRef(
  ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
    const userCompany = useUserCompany();
    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null,
    );

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    const initialFormState = {
      bnkId: null,
      bnkCode: "",
      bnkName: "",
      bnkDescription: "",
      bnkStatus: 2, // Default to Pending/Inactive or 2
      bnkActive: 1, // Default to 1 (Active)
      createdBy: "",
      createdDate: null,
      lastModifiedBy: "",
      lastModifiedDate: null,
      bnkCompany: company,
    };

    const [bankData, setBankData] = useState(
      editMode && currentValues
        ? {
            ...initialFormState,
            ...currentValues,
            bnkCompany: company,
          }
        : initialFormState,
    );

    const [currentErrors, setCurrentErrors] = useState({});

    useEffect(() => {
      const errObj = errors?.bankMasterErrors ? errors.bankMasterErrors : (errors || {});
      setCurrentErrors(errObj);
    }, [errors]);

    const normalizeCompanyValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      if (typeof value === "number") {
        return value;
      }
      const asNumber = Number(value);
      if (!Number.isNaN(asNumber)) {
        return asNumber;
      }
      const matchingOption = (dropdown.companyOptions || []).find(
        (option) => option.text === value || option.key === value,
      );
      return matchingOption ? matchingOption.value : value;
    };

    useEffect(() => {
      if (editMode && currentValues) {
        setBankData({
          ...initialFormState,
          ...currentValues,
          bnkCompany: normalizeCompanyValue(currentValues.bnkCompany) || company,
        });
      } else if (!editMode) {
        setBankData((prev) => ({
          ...prev,
          bnkCompany: company,
        }));
      }
    }, [editMode, currentValues, company, dropdown.companyOptions]);

    useImperativeHandle(ref, () => ({
      getData: () => bankData,
      validateForm: () => {
        const validationErrors = bankValidation(bankData);
        setCurrentErrors(validationErrors);
        return validationErrors;
      },
      resetForm: () => {
        setBankData(initialFormState);
        setCurrentErrors({});
      },
    }));

    const handleInputChange = (field, value) => {
      setBankData((prevState) => ({
        ...prevState,
        [field]: value,
      }));
      // Clear the error for this field
      setCurrentErrors((prevState) => {
        const { [field]: _, ...rest } = prevState;
        return rest;
      });
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Bank Information</h4>

              <div className="field">
                <Input
                  label="Bank Code"
                  placeholder="Bank Code"
                  module={MODULES.Master}
                  form={FORM}
                  field="bnk_code"
                  editMode={editMode}
                  disabled={true}
                  readOnly={true}
                  name="bnkCode"
                  value={bankData.bnkCode || ""}
                />
              </div>
              <div className="two fields">
                <Input
                  required={true}
                  label="Bank Name"
                  placeholder="Bank Name"
                  module={MODULES.Master}
                  form={FORM}
                  field="bnk_name"
                  editMode={editMode}
                  error={currentErrors.bnkName}
                  resetError={() =>
                    handleInputChange("bnkName", bankData.bnkName)
                  }
                  name="bnkName"
                  value={bankData.bnkName || ""}
                  onChange={(e) => handleInputChange("bnkName", e.target.value)}
                  blockPattern={/[^a-zA-Z\s&_\-()]/g}
                  duplicate={false}
                  activeCol="bnk_active"
                  companyCol="bnk_company"
                  companyWise={true}
                  tableName="masterdata.tms_m_banks"
                  primaryKey="bnk_id"
                  searchField="bnk_name"
                  keyType="S"
                  duplicateErrorMessage="Bank with this name already exists for the selected company"
                  setErrors={setCurrentErrors}
                  errorField="bnkName"
                  id={bankData.bnkId}
                />

                <Input
                  label="Description"
                  placeholder="Description"
                  module={MODULES.Master}
                  form={FORM}
                  field="bnk_description"
                  editMode={editMode}
                  error={currentErrors.bnkDescription}
                  resetError={() =>
                    handleInputChange(
                      "bnkDescription",
                      bankData.bnkDescription,
                    )
                  }
                  name="bnkDescription"
                  value={bankData.bnkDescription || ""}
                  onChange={(e) =>
                    handleInputChange("bnkDescription", e.target.value)
                  }
                />
              </div>

              <div className="three fields">
                {/* Company (Disabled) */}
                <Input
                  label="Company"
                  placeholder="Company"
                  module={MODULES.Master}
                  form={FORM}
                  field="bnk_company"
                  editMode={editMode}
                  disabled={true}
                  readOnly={true}
                  name="bnkCompany"
                  value={
                    (dropdown.companyOptions || []).find(
                      (option) =>
                        Number(option.value) === Number(bankData.bnkCompany) ||
                        option.key === bankData.bnkCompany,
                    )?.text ||
                    userCompany?.comCompany ||
                    userCompany?.comName ||
                    ""
                  }
                />

                {/* Status Dropdown */}
                <Dropdown
                  required={true}
                  label="Status"
                  placeholder="Status"
                  module={MODULES.Master}
                  form={FORM}
                  field="bnk_status"
                  editMode={editMode}
                  error={currentErrors.bnkStatus}
                  resetError={() =>
                    handleInputChange("bnkStatus", bankData.bnkStatus)
                  }
                  options={dropdown.statusOptions || []}
                  value={
                    bankData.bnkStatus !== undefined
                      ? bankData.bnkStatus
                      : ""
                  }
                  style={{ height: "20px" }}
                  onChange={(e, data) =>
                    handleInputChange("bnkStatus", data.value)
                  }
                />

                {/* Active Dropdown */}
                <Dropdown
                  required={true}
                  label="Active"
                  placeholder="Active"
                  module={MODULES.Master}
                  form={FORM}
                  field="bnk_active"
                  editMode={editMode}
                  error={currentErrors.bnkActive}
                  resetError={() =>
                    handleInputChange("bnkActive", bankData.bnkActive)
                  }
                  options={dropdown.activeOptions || []}
                  value={
                    bankData.bnkActive !== undefined
                      ? bankData.bnkActive
                      : ""
                  }
                  style={{ height: "20px" }}
                  onChange={(e, data) =>
                    handleInputChange("bnkActive", data.value)
                  }
                />
              </div>
            </div>

            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: bankData.createdBy
                      ? bankData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: bankData.createdDate
                      ? formatFromJsonDate(bankData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: bankData.lastModifiedBy
                      ? bankData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: bankData.lastModifiedDate
                      ? formatFromJsonDate(bankData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  },
);

export default BankAddComponent;
